import { create } from 'zustand';
import {
  lerAccessToken,
  tokenExpirado,
  type IdentidadeDoToken,
  type VinculoDoToken,
} from '../servicos/jwt';

/**
 * Estado de sessão: tokens, identidade e **empresa corrente**.
 *
 * ## RN-603 — correção obrigatória
 *
 * O legado fazia `setCurrentBusiness(user.business[0])` em todo caminho de
 * autenticação: a primeira empresa do array "ganhava", sem seletor e sem o
 * usuário saber. Quem tinha dois vínculos operava um deles achando que via
 * tudo — e o backend ainda contava notificações olhando só essa posição
 * (RN-404), então metade das aprovações simplesmente não aparecia.
 *
 * Aqui a empresa corrente é **escolha explícita**. Com um vínculo, ele é
 * assumido (não há o que perguntar). Com mais de um, o estado fica em
 * `precisaEscolherEmpresa` e a aplicação pergunta antes de mostrar qualquer
 * número. Trocar depois é pela janela "Trocar empresa" da barra superior, que
 * lista os vínculos mesmo quando há um só. A semântica é a mesma de `resolverEmpresaCorrente`
 * (`packages/domain/src/autorizacao/papeis.ts`), reimplementada aqui porque o
 * web não importa o domínio (`docs/arquitetura.md`) — mas a decisão que vale é
 * sempre a do servidor: o id enviado é revalidado contra o token a cada
 * requisição (`AutorizacaoService.exigirEmpresaSolicitada`, RN-137).
 */

/** Resposta de `POST /api/auth/login` e `POST /api/auth/token`. */
export interface TokensSessao {
  readonly accessToken: string;
  readonly refreshToken: string;
  /** TTL declarado do access token (ex.: `"15m"`) — **não** é uma data. */
  readonly expiraEm: string;
}

/** O que a sessão guarda, sem as ações. */
interface DadosSessao {
  readonly accessToken: string | null;
  /** Rotativo: cada renovação devolve um novo e invalida o anterior. */
  readonly refreshToken: string | null;
  readonly identidade: IdentidadeDoToken | null;
  readonly empresaCorrente: string | null;
}

export interface EstadoSessao extends DadosSessao {
  /** Preenchido quando a sessão cai sozinha, para a tela de login explicar. */
  readonly motivoEncerramento: string | null;

  iniciar: (tokens: TokensSessao) => void;
  aplicarRenovacao: (tokens: TokensSessao) => void;
  escolherEmpresa: (businessId: string) => boolean;
  /** Devolve o usuário ao seletor. Só faz sentido com mais de um vínculo. */
  limparEmpresa: () => void;
  encerrar: (motivo?: string) => void;
}

const CHAVE_ARMAZENAMENTO = 'deca.sessao.v1';

/**
 * Declarado **antes** do `create`: o inicializador do store roda na importação
 * do módulo e chega aqui por `restaurarSessao()`. Uma `const` declarada depois
 * estouraria em TDZ no boot — e o typecheck não vê isso.
 */
const VAZIO = {
  accessToken: null,
  refreshToken: null,
  identidade: null,
  empresaCorrente: null,
} as const;

export const usarSessao = create<EstadoSessao>((set, get) => ({
  ...restaurarSessao(),
  motivoEncerramento: null,

  iniciar: (tokens) => {
    const identidade = lerAccessToken(tokens.accessToken);
    if (identidade === null) {
      // Token que não é um JWT legível não vira sessão: seguir com ele daria
      // uma tela "logada" que falha em toda requisição, sem explicação.
      set({ ...VAZIO, motivoEncerramento: 'O servidor devolveu um token ilegível.' });
      persistir(null);
      return;
    }

    const empresaCorrente = empresaAssumida(identidade.vinculos);
    set({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      identidade,
      empresaCorrente,
      motivoEncerramento: null,
    });
    persistir({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      empresaCorrente,
    });
  },

  aplicarRenovacao: (tokens) => {
    const identidade = lerAccessToken(tokens.accessToken);
    if (identidade === null) {
      set({ ...VAZIO, motivoEncerramento: 'O servidor devolveu um token ilegível.' });
      persistir(null);
      return;
    }

    // A renovação pode trazer vínculos diferentes (alguém perdeu acesso a uma
    // empresa enquanto a aba estava aberta). Se a escolhida saiu da lista, ela
    // é descartada — continuar exibindo dados de uma empresa não mais vinculada
    // é justamente o tipo de silêncio que o RN-603 criou.
    const anterior = get().empresaCorrente;
    const aindaVinculada =
      anterior !== null && identidade.vinculos.some((v) => v.businessId === anterior);
    const empresaCorrente = aindaVinculada ? anterior : empresaAssumida(identidade.vinculos);

    set({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      identidade,
      empresaCorrente,
      motivoEncerramento: null,
    });
    persistir({
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      empresaCorrente,
    });
  },

  escolherEmpresa: (businessId) => {
    const { identidade, accessToken, refreshToken } = get();
    const permitida = identidade?.vinculos.some((v) => v.businessId === businessId) ?? false;
    if (!permitida) return false;

    set({ empresaCorrente: businessId });
    if (accessToken !== null && refreshToken !== null) {
      persistir({ accessToken, refreshToken, empresaCorrente: businessId });
    }
    return true;
  },

  limparEmpresa: () => {
    const { accessToken, refreshToken } = get();
    set({ empresaCorrente: null });
    if (accessToken !== null && refreshToken !== null) {
      persistir({ accessToken, refreshToken, empresaCorrente: null });
    }
  },

  encerrar: (motivo) => {
    set({ ...VAZIO, motivoEncerramento: motivo ?? null });
    persistir(null);
  },
}));

/** Um vínculo é assumido; mais de um exige escolha explícita (RN-603). */
function empresaAssumida(vinculos: readonly VinculoDoToken[]): string | null {
  return vinculos.length === 1 ? (vinculos[0]?.businessId ?? null) : null;
}

// ─── Seletores ──────────────────────────────────────────────────────────────
// Funções puras sobre o estado, para as telas não repetirem a mesma lógica.

export function estaAutenticado(estado: EstadoSessao): boolean {
  return estado.refreshToken !== null && estado.identidade !== null;
}

/** RN-603 — mais de um vínculo e nenhuma escolha feita. */
export function precisaEscolherEmpresa(estado: EstadoSessao): boolean {
  if (!estaAutenticado(estado)) return false;
  return estado.empresaCorrente === null && (estado.identidade?.vinculos.length ?? 0) > 1;
}

/** Conta sem nenhum vínculo ativo: nada a mostrar, e não é culpa do usuário. */
export function semVinculo(estado: EstadoSessao): boolean {
  return estaAutenticado(estado) && (estado.identidade?.vinculos.length ?? 0) === 0;
}

export function vinculoCorrente(estado: EstadoSessao): VinculoDoToken | null {
  if (estado.empresaCorrente === null) return null;
  return estado.identidade?.vinculos.find((v) => v.businessId === estado.empresaCorrente) ?? null;
}

// ─── Persistência ───────────────────────────────────────────────────────────

interface SessaoPersistida {
  accessToken: string;
  refreshToken: string;
  empresaCorrente: string | null;
}

/**
 * Recarrega a sessão do `localStorage` **validando antes de reusar** (RN-605).
 *
 * O access token expirado não derruba a sessão como no legado: o refresh é
 * rotativo e de validade longa, então o cliente HTTP renova na primeira
 * requisição e o usuário não é obrigado a digitar a senha de novo a cada 15
 * minutos. O que descarta a sessão é token **ilegível** ou refresh ausente —
 * aí não há como renovar nada.
 *
 * Sobre guardar token em `localStorage`: fica legível a qualquer script na
 * origem, então o custo de um XSS é a sessão. A alternativa (cookie
 * `httpOnly` + CSRF) exige mudança no backend e está registrada como pendência
 * no INTEGRACAO.md.
 */
function restaurarSessao(): DadosSessao {
  const bruto = lerArmazenamento();
  if (bruto === null) return { ...VAZIO };

  const identidade = lerAccessToken(bruto.accessToken);
  if (identidade === null) {
    persistir(null);
    return { ...VAZIO };
  }

  // Vínculo persistido que sumiu do token não é reusado.
  const empresaValida =
    bruto.empresaCorrente !== null &&
    identidade.vinculos.some((v) => v.businessId === bruto.empresaCorrente);

  return {
    accessToken: bruto.accessToken,
    refreshToken: bruto.refreshToken,
    identidade,
    empresaCorrente: empresaValida ? bruto.empresaCorrente : empresaAssumida(identidade.vinculos),
  };
}

function lerArmazenamento(): SessaoPersistida | null {
  try {
    const texto = window.localStorage.getItem(CHAVE_ARMAZENAMENTO);
    if (texto === null) return null;

    const dado: unknown = JSON.parse(texto);
    if (typeof dado !== 'object' || dado === null) return null;

    const { accessToken, refreshToken, empresaCorrente } = dado as Record<string, unknown>;
    if (typeof accessToken !== 'string' || typeof refreshToken !== 'string') return null;

    return {
      accessToken,
      refreshToken,
      empresaCorrente: typeof empresaCorrente === 'string' ? empresaCorrente : null,
    };
  } catch {
    // JSON corrompido ou storage bloqueado (modo privado, cota): sem sessão.
    return null;
  }
}

function persistir(sessao: SessaoPersistida | null): void {
  try {
    if (sessao === null) {
      window.localStorage.removeItem(CHAVE_ARMAZENAMENTO);
      return;
    }
    window.localStorage.setItem(CHAVE_ARMAZENAMENTO, JSON.stringify(sessao));
  } catch {
    // Storage indisponível não pode derrubar a aplicação: a sessão continua
    // válida em memória até a aba fechar.
  }
}

/** Access token que já expirou (ou está prestes a) — usado pelo cliente HTTP. */
export function acessoPrecisaRenovar(estado: EstadoSessao): boolean {
  if (estado.refreshToken === null) return false;
  if (estado.accessToken === null || estado.identidade === null) return true;
  return tokenExpirado(estado.identidade);
}
